import React from 'react';

import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router';

import { PageSubHeaderBreadcrumbs } from '@/components/organisms/PageSubHeader';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { selectAccountSlug } from '@/redux/session/selectors';

// Keep only the last breadcrumb level below 1024px; synced with the detail header breakpoint.
const CAMPAIGNS_FIRST_LEVEL_COLLAPSE_CLASS = 'max-lg:hidden';

interface CampaignBreadcrumbsProps {
  campaignName?: string;
  currentStep?: string;
}

export const CampaignBreadcrumbs = React.memo(
  ({ campaignName, currentStep }: CampaignBreadcrumbsProps) => {
    const { t } = useTranslation();
    const slug = useSelector(selectAccountSlug);
    const { campaignId } = useParams<{ campaignId: string }>();
    const location = useLocation();

    const isAudience = location.pathname.endsWith('/audience');

    const getBackPath = () => {
      if (!currentStep || !campaignId) {
        return `/${slug}/campaigns`;
      }
      if (isAudience) {
        return `/${slug}/campaigns/${campaignId}/setup`;
      }
      return `/${slug}/campaigns/${campaignId}/messaging`;
    };
    const backPath = getBackPath();
    const name = campaignName || (!campaignId ? t('campaigns.new_campaign') : '');

    const setupPath = `/${slug}/campaigns/${campaignId}/setup`;
    return (
      <PageSubHeaderBreadcrumbs>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <div className="flex items-center">
                <Link to={backPath}>
                  <Button variant="ghost-neutral" size="icon-sm">
                    <ArrowLeftIcon size={16} className="text-muted-foreground" />
                  </Button>
                </Link>
                <div className="mr-4 ml-2 h-4 w-px bg-border" />
                <BreadcrumbLink asChild>
                  <Link
                    to={`/${slug}/campaigns`}
                    className={cn('breadcrumb-link', CAMPAIGNS_FIRST_LEVEL_COLLAPSE_CLASS)}
                  >
                    {t('common.campaigns')}
                  </Link>
                </BreadcrumbLink>
              </div>
            </BreadcrumbItem>

            {/* Show campaign name if we're on a sub-page */}
            {currentStep && (
              <>
                <BreadcrumbSeparator className={CAMPAIGNS_FIRST_LEVEL_COLLAPSE_CLASS} />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={setupPath} className="breadcrumb-link">
                      {name}
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{currentStep}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}

            {/* Show only campaign name if we're on main campaign page */}
            {!currentStep && (
              <>
                <BreadcrumbSeparator className={CAMPAIGNS_FIRST_LEVEL_COLLAPSE_CLASS} />
                <BreadcrumbItem>
                  <BreadcrumbPage>{name}</BreadcrumbPage>
                </BreadcrumbItem>
              </>
            )}
          </BreadcrumbList>
        </Breadcrumb>
      </PageSubHeaderBreadcrumbs>
    );
  }
);

CampaignBreadcrumbs.displayName = 'CampaignBreadcrumbs';
